import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Plus, Trash2, Save, Loader2, Eye, Pause, Play, Copy,
  ArrowLeft, X, Image, GripVertical, Sparkles,
  BarChart3, Users, MousePointerClick, TrendingUp, RefreshCw,
} from "lucide-react";

const API = "/api";
const SITE_URL = window.location.origin;

const TEMPLATES = [
  { id: "energy", label: "Green Energy", desc: "Verde, eco-friendly", color: "bg-emerald-500" },
  { id: "premium", label: "Premium", desc: "Scuro, elegante, dorato", color: "bg-slate-900" },
  { id: "flash", label: "Offerta Flash", desc: "Rosso, urgenza", color: "bg-red-500" },
  { id: "tech", label: "Tech", desc: "Blu, moderno", color: "bg-blue-600" },
  { id: "minimal", label: "Minimal", desc: "Pulito, bianco", color: "bg-slate-200" },
  { id: "solar", label: "Solar", desc: "Arancio solare, caldo", color: "bg-orange-500" },
  { id: "nature", label: "Natura", desc: "Terra e foresta", color: "bg-green-800" },
  { id: "ocean", label: "Oceano", desc: "Teal e acqua, idro", color: "bg-teal-500" },
  { id: "sunset", label: "Tramonto", desc: "Corallo e ambra", color: "bg-rose-500" },
  { id: "futuristic", label: "Futuristico", desc: "Neon elettrico, scuro", color: "bg-cyan-500" },
];

const emptyFeature = () => ({ title: "", description: "" });

export default function AdminLandings() {
  const [landings, setLandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const fetchLandings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/landings`);
      setLandings(res.data);
    } catch { toast.error("Errore caricamento landing"); }
    finally { setLoading(false); }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch {}
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/landings/analytics/summary`);
      setAnalytics(res.data);
    } catch { toast.error("Errore caricamento analytics"); }
    finally { setAnalyticsLoading(false); }
  }, []);

  useEffect(() => { fetchLandings(); fetchProducts(); }, [fetchLandings, fetchProducts]);

  useEffect(() => { if (showAnalytics) fetchAnalytics(); }, [showAnalytics, fetchAnalytics]);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa landing?")) return;
    try {
      await axios.delete(`${API}/landings/${id}`);
      toast.success("Landing eliminata");
      fetchLandings();
    } catch { toast.error("Errore eliminazione"); }
  };

  const handleToggleStatus = async (landing) => {
    const newStatus = landing.status === "active" ? "paused" : "active";
    try {
      await axios.put(`${API}/landings/${landing.id}`, { status: newStatus });
      toast.success(newStatus === "active" ? "Landing attivata" : "Landing in pausa");
      fetchLandings();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const startNew = () => {
    setEditing({
      title: "", subtitle: "", hero_image_url: "", product_image_url: "", template_style: "energy",
      product_id: "", product_name: "", product_brand: "", product_category: "",
      product_description: "", product_specs: "", full_price: "", promo_price: "",
      price_note: "", price_display: "price", max_savings: "",
      features: [emptyFeature(), emptyFeature(), emptyFeature()],
      gallery_images: [], cta_text: "Richiedi Preventivo", show_timer: true,
      brochure_url: "", calculator_type: "", form_type: "classic", otp_required: false,
      cta_result_text: "Ricevi un Preventivo Gratuito",
      show_features: true, features_title: "", show_specs: true, specs_title: "",
      show_gallery: true, gallery_title: "",
      status: "active", create_product: false, tracking_codes: [],
    });
  };

  const linkProduct = (productId) => {
    const p = products.find((x) => x.id === productId);
    if (p) {
      setEditing((prev) => ({
        ...prev, product_id: p.id, product_name: p.name, product_brand: p.brand || "",
        product_category: p.category || "", product_description: p.description || "",
        product_specs: p.specs || "", gallery_images: p.images || [],
        brochure_url: p.brochure_url || "", hero_image_url: p.images?.[0] || "",
      }));
    }
  };

  if (editing) {
    return (
      <LandingEditor
        data={editing}
        products={products}
        onLinkProduct={linkProduct}
        onSave={async (data) => {
          try {
            if (data.id) {
              const { id, slug, created_at, updated_at, create_product, ...rest } = data;
              await axios.put(`${API}/landings/${id}`, rest);
              toast.success("Landing aggiornata");
            } else {
              await axios.post(`${API}/landings`, data);
              toast.success("Landing creata!");
            }
            setEditing(null);
            fetchLandings();
            fetchProducts();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Errore salvataggio");
          }
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div data-testid="admin-landings">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Landing Promozionali ({landings.length})
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAnalytics(!showAnalytics)}
            className={`flex items-center gap-2 font-bold rounded-full px-5 py-2.5 text-sm transition-colors ${showAnalytics ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid="toggle-analytics-btn">
            <BarChart3 className="w-4 h-4" /> Analytics
          </button>
          <button onClick={startNew}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-5 py-2.5 text-sm btn-scale flex items-center gap-2"
            data-testid="create-landing-btn">
            <Plus className="w-4 h-4" /> Nuova Landing
          </button>
        </div>
      </div>

      {/* Analytics Table */}
      {showAnalytics && (
        <div className="mb-8 bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid="analytics-section">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Performance Landing</h3>
                <p className="text-[11px] text-slate-400">Visite, lead e tassi di conversione</p>
              </div>
            </div>
            <button onClick={fetchAnalytics} disabled={analyticsLoading}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
              data-testid="refresh-analytics-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${analyticsLoading ? "animate-spin" : ""}`} /> Aggiorna
            </button>
          </div>

          {analyticsLoading && !analytics ? (
            <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : analytics ? (
            <>
              {/* Totals row */}
              <div className="grid grid-cols-3 gap-4 px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-3" data-testid="analytics-total-views">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    <MousePointerClick className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{analytics.totals.views}</div>
                    <div className="text-[11px] text-slate-400">Visite Totali</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-testid="analytics-total-leads">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Users className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{analytics.totals.leads}</div>
                    <div className="text-[11px] text-slate-400">Lead Totali</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-testid="analytics-total-rate">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{analytics.totals.conversion_rate}%</div>
                    <div className="text-[11px] text-slate-400">Conversione Media</div>
                  </div>
                </div>
              </div>

              {/* Table */}
              {analytics.landings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="analytics-table">
                    <thead>
                      <tr className="bg-slate-50 text-left">
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Landing</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Template</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Stato</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">Visite</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">Lead</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">Conversione</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {analytics.landings.map((item) => {
                        const tmpl = TEMPLATES.find((t) => t.id === item.template_style) || TEMPLATES[0];
                        return (
                          <tr key={item.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`analytics-row-${item.slug}`}>
                            <td className="px-5 py-3.5">
                              <div className="font-semibold text-slate-900 text-sm">{item.title}</div>
                              <div className="text-[11px] text-slate-400">/promo/{item.slug}</div>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${tmpl.color}`} />
                                <span className="text-xs font-medium text-slate-600">{tmpl.label}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${item.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                                {item.status === "active" ? "Attiva" : "In pausa"}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-right">
                              <span className="font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{item.views}</span>
                            </td>
                            <td className="px-5 py-3.5 text-right">
                              <span className="font-bold text-emerald-600" style={{ fontFamily: "Outfit, sans-serif" }}>{item.leads}</span>
                            </td>
                            <td className="px-5 py-3.5 text-right">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${item.conversion_rate >= 5 ? "bg-emerald-50 text-emerald-700" : item.conversion_rate > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-400"}`}>
                                {item.conversion_rate}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-xs text-slate-400">Nessun dato disponibile</div>
              )}
            </>
          ) : null}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
      ) : landings.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
          <Sparkles className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-4">Nessuna landing creata</p>
          <button onClick={startNew} className="text-sm font-semibold text-emerald-600 hover:text-emerald-700">+ Crea la prima landing</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {landings.map((l) => {
            const tmpl = TEMPLATES.find((t) => t.id === l.template_style) || TEMPLATES[0];
            return (
              <div key={l.id} className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`landing-card-${l.slug}`}>
                <div className={`h-2 ${tmpl.color}`} />
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${l.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                        {l.status === "active" ? "Attiva" : "In pausa"}
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 ml-2">{tmpl.label}</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-sm text-slate-900 mb-1 line-clamp-1" style={{ fontFamily: "Outfit, sans-serif" }}>{l.title}</h3>
                  <p className="text-xs text-slate-500 mb-3 line-clamp-2">{l.subtitle || l.product_description}</p>
                  {l.promo_price && (
                    <div className="flex items-center gap-2 mb-3">
                      {l.full_price && <span className="text-xs text-slate-400 line-through">{l.full_price}</span>}
                      <span className="text-sm font-bold text-red-600">{l.promo_price}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 pt-3 border-t border-slate-50">
                    <button onClick={() => setEditing(l)} className="flex-1 text-xs font-semibold text-slate-600 hover:text-slate-800 py-1.5 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`edit-landing-${l.slug}`}>
                      Modifica
                    </button>
                    <button onClick={() => handleToggleStatus(l)} className="p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-amber-600 transition-colors" title={l.status === "active" ? "Metti in pausa" : "Attiva"}>
                      {l.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <a href={`${SITE_URL}/promo/${l.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-emerald-600 transition-colors" title="Anteprima">
                      <Eye className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={() => { navigator.clipboard.writeText(`${SITE_URL}/promo/${l.slug}`); toast.success("Link copiato!"); }}
                      className="p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-blue-600 transition-colors" title="Copia link">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(l.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors" title="Elimina">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LandingEditor({ data, products, onLinkProduct, onSave, onCancel }) {
  const [form, setForm] = useState({ ...data });
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const setFeature = (idx, key, val) => {
    const f = [...form.features];
    f[idx] = { ...f[idx], [key]: val };
    set("features", f);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error("Il titolo e obbligatorio"); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const addGalleryImage = () => {
    if (!imageUrl) return;
    set("gallery_images", [...form.gallery_images, imageUrl]);
    setImageUrl("");
  };

  return (
    <div data-testid="landing-editor">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Torna alla lista
      </button>

      <div className="bg-white rounded-xl border border-slate-100 p-6 space-y-6">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          {form.id ? "Modifica Landing" : "Nuova Landing Promozionale"}
        </h2>

        {/* Link prodotto esistente */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">Collega a Prodotto Esistente (opzionale)</label>
          <select value={form.product_id || ""} onChange={(e) => { if (e.target.value) onLinkProduct(e.target.value); else set("product_id", ""); }}
            className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="landing-link-product">
            <option value="">-- Crea da zero --</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.category})</option>)}
          </select>
          {!form.product_id && !form.id && (
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-500">
              <input type="checkbox" checked={form.create_product} onChange={(e) => set("create_product", e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600" data-testid="landing-create-product" />
              Crea anche la scheda prodotto nel catalogo
            </label>
          )}
        </div>

        {/* Template */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">Stile Template</label>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((t) => (
              <button key={t.id} onClick={() => set("template_style", t.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border-2 transition-all ${form.template_style === t.id ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900/10" : "border-slate-200 hover:border-slate-300"}`}
                data-testid={`landing-tmpl-${t.id}`}>
                <div className={`w-4 h-4 rounded-full ${t.color}`} />
                <div className="text-left">
                  <div className="font-bold">{t.label}</div>
                  <div className="text-slate-400">{t.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Hero */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Titolo Landing *</label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="h-10 rounded-xl" placeholder="Pannello Solare XYZ 450W" data-testid="landing-title" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Sottotitolo</label>
            <Input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="h-10 rounded-xl" placeholder="La rivoluzione dell'energia solare..." data-testid="landing-subtitle" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Immagine Sfondo Hero (URL)</label>
            <Input value={form.hero_image_url} onChange={(e) => set("hero_image_url", e.target.value)} className="h-10 rounded-xl" placeholder="https://... (sfondo della sezione hero)" data-testid="landing-hero-image" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Immagine Prodotto (accanto al titolo)</label>
            <Input value={form.product_image_url || ""} onChange={(e) => set("product_image_url", e.target.value)} className="h-10 rounded-xl" placeholder="https://... (immagine visibile nel hero)" data-testid="landing-product-image" />
          </div>
        </div>

        {/* Product Info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nome Prodotto</label>
            <Input value={form.product_name} onChange={(e) => set("product_name", e.target.value)} className="h-10 rounded-xl" placeholder="Nome prodotto" data-testid="landing-product-name" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Brand</label>
            <Input value={form.product_brand} onChange={(e) => set("product_brand", e.target.value)} className="h-10 rounded-xl" placeholder="SunPower, LG, ..." data-testid="landing-product-brand" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Categoria</label>
            <select value={form.product_category} onChange={(e) => set("product_category", e.target.value)}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="landing-product-category">
              <option value="">Seleziona...</option>
              <option value="Pannelli">Pannelli</option>
              <option value="Inverter">Inverter</option>
              <option value="Batterie">Batterie</option>
              <option value="Kit Completo">Kit Completo</option>
              <option value="Accessori">Accessori</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Descrizione Prodotto</label>
          <textarea value={form.product_description} onChange={(e) => set("product_description", e.target.value)}
            rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none" placeholder="Descrizione del prodotto..." data-testid="landing-product-desc" />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Specifiche Tecniche (una per riga, formato "Chiave: Valore")</label>
          <textarea value={form.product_specs} onChange={(e) => set("product_specs", e.target.value)}
            rows={4} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono resize-none" placeholder="Potenza: 450W&#10;Efficienza: 22.5%&#10;Garanzia: 25 anni" data-testid="landing-product-specs" />
        </div>

        {/* Price / Savings Display */}
        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
          <div className="mb-3">
            <h3 className="text-sm font-bold text-red-800 mb-1.5">Cosa mostrare nella Hero</h3>
            <select value={form.price_display || "price"} onChange={(e) => set("price_display", e.target.value)}
              className="flex h-10 w-full sm:w-72 rounded-xl border border-input bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="landing-price-display">
              <option value="price">Prezzo (Pieno + Promo)</option>
              <option value="savings">Risparmio Massimo Annuo</option>
              <option value="none">Nessuno</option>
            </select>
          </div>

          {form.price_display === "savings" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-emerald-700 mb-1 block">Risparmio Massimo</label>
                <Input value={form.max_savings || ""} onChange={(e) => set("max_savings", e.target.value)} className="h-10 rounded-xl" placeholder="1.500€/anno" data-testid="landing-max-savings" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nota (opzionale)</label>
                <Input value={form.price_note} onChange={(e) => set("price_note", e.target.value)} className="h-10 rounded-xl" placeholder="Stima basata su consumi medi" data-testid="landing-price-note" />
              </div>
            </div>
          ) : form.price_display !== "none" ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-red-700 mb-1 block">Prezzo Pieno</label>
                <Input value={form.full_price} onChange={(e) => set("full_price", e.target.value)} className="h-10 rounded-xl" placeholder="€2.499" data-testid="landing-full-price" />
              </div>
              <div>
                <label className="text-xs font-semibold text-red-700 mb-1 block">Prezzo Promo</label>
                <Input value={form.promo_price} onChange={(e) => set("promo_price", e.target.value)} className="h-10 rounded-xl" placeholder="€1.799" data-testid="landing-promo-price" />
              </div>
              <div>
                <label className="text-xs font-semibold text-red-700 mb-1 block">Nota Prezzo</label>
                <Input value={form.price_note} onChange={(e) => set("price_note", e.target.value)} className="h-10 rounded-xl" placeholder="IVA inclusa" data-testid="landing-price-note" />
              </div>
            </div>
          ) : null}
        </div>

        {/* ── Section Toggles ── */}
        <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-bold text-indigo-900">Sezioni Visibili nella Landing</h3>
          <p className="text-xs text-slate-400 -mt-2">Attiva/disattiva le sezioni e personalizza i titoli.</p>

          {/* Features toggle */}
          <div className={`flex items-start gap-3 p-3 rounded-xl border ${form.show_features !== false ? "border-indigo-200 bg-white" : "border-slate-100 bg-slate-50/50"}`}>
            <input type="checkbox" checked={form.show_features !== false} onChange={(e) => set("show_features", e.target.checked)}
              className="mt-1 h-4 w-4 rounded text-indigo-600" data-testid="toggle-features" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-slate-700">Vantaggi / Caratteristiche</span>
              {form.show_features !== false && (
                <Input value={form.features_title || ""} onChange={(e) => set("features_title", e.target.value)}
                  className="h-8 rounded-lg text-xs mt-1.5" placeholder="Titolo (default: Perche Scegliere Questo Prodotto)" data-testid="features-title-input" />
              )}
            </div>
          </div>

          {/* Specs toggle */}
          <div className={`flex items-start gap-3 p-3 rounded-xl border ${form.show_specs !== false ? "border-indigo-200 bg-white" : "border-slate-100 bg-slate-50/50"}`}>
            <input type="checkbox" checked={form.show_specs !== false} onChange={(e) => set("show_specs", e.target.checked)}
              className="mt-1 h-4 w-4 rounded text-indigo-600" data-testid="toggle-specs" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-slate-700">Specifiche Tecniche</span>
              {form.show_specs !== false && (
                <Input value={form.specs_title || ""} onChange={(e) => set("specs_title", e.target.value)}
                  className="h-8 rounded-lg text-xs mt-1.5" placeholder="Titolo (default: Specifiche Tecniche)" data-testid="specs-title-input" />
              )}
            </div>
          </div>

          {/* Gallery toggle */}
          <div className={`flex items-start gap-3 p-3 rounded-xl border ${form.show_gallery !== false ? "border-indigo-200 bg-white" : "border-slate-100 bg-slate-50/50"}`}>
            <input type="checkbox" checked={form.show_gallery !== false} onChange={(e) => set("show_gallery", e.target.checked)}
              className="mt-1 h-4 w-4 rounded text-indigo-600" data-testid="toggle-gallery" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-slate-700">Galleria Immagini</span>
              {form.show_gallery !== false && (
                <Input value={form.gallery_title || ""} onChange={(e) => set("gallery_title", e.target.value)}
                  className="h-8 rounded-lg text-xs mt-1.5" placeholder="Titolo (default: Galleria)" data-testid="gallery-title-input" />
              )}
            </div>
          </div>
        </div>

        {/* Features Content (only if enabled) */}
        {form.show_features !== false && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-slate-700">Vantaggi / Caratteristiche</label>
            <button onClick={() => set("features", [...form.features, emptyFeature()])} className="text-xs text-emerald-600 font-semibold hover:text-emerald-700">+ Aggiungi</button>
          </div>
          <div className="space-y-2">
            {form.features.map((f, i) => (
              <div key={i} className="flex gap-2 items-start">
                <GripVertical className="w-4 h-4 text-slate-300 mt-2.5 shrink-0" />
                <Input value={f.title} onChange={(e) => setFeature(i, "title", e.target.value)} className="h-9 rounded-lg text-sm flex-1" placeholder="Titolo vantaggio" />
                <Input value={f.description} onChange={(e) => setFeature(i, "description", e.target.value)} className="h-9 rounded-lg text-sm flex-[2]" placeholder="Descrizione" />
                <button onClick={() => set("features", form.features.filter((_, j) => j !== i))} className="p-2 text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Gallery Content (only if enabled) */}
        {form.show_gallery !== false && (
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">Galleria Immagini</label>
          <div className="flex gap-2 mb-2">
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="h-9 rounded-lg text-sm flex-1" placeholder="URL immagine..." data-testid="landing-gallery-url" />
            <button onClick={addGalleryImage} className="bg-slate-900 text-white px-3 rounded-lg text-xs font-bold h-9">
              <Image className="w-3.5 h-3.5" />
            </button>
          </div>
          {form.gallery_images.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {form.gallery_images.map((img, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 group">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set("gallery_images", form.gallery_images.filter((_, j) => j !== i))}
                    className="absolute inset-0 bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Brochure + CTA + Timer */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">URL Brochure PDF</label>
            <Input value={form.brochure_url} onChange={(e) => set("brochure_url", e.target.value)} className="h-10 rounded-xl" placeholder="https://...pdf" data-testid="landing-brochure" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">CTA Pulsante Invio</label>
            <Input value={form.cta_text} onChange={(e) => set("cta_text", e.target.value)} className="h-10 rounded-xl" placeholder="Richiedi Preventivo" data-testid="landing-cta-text" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
              <input type="checkbox" checked={form.show_timer} onChange={(e) => set("show_timer", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600" data-testid="landing-timer-toggle" />
              Timer urgenza giornaliero
            </label>
          </div>
        </div>
        {form.form_type === "calculator" && (
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">CTA Pagina Risultato Calcolo</label>
            <p className="text-xs text-slate-400 mb-2">Il pulsante che appare dopo il calcolo del risparmio, prima del form dati.</p>
            <Input value={form.cta_result_text || ""} onChange={(e) => set("cta_result_text", e.target.value)} className="h-10 rounded-xl sm:w-96" placeholder="Ricevi un Preventivo Gratuito" data-testid="landing-cta-result-text" />
          </div>
        )}

        {/* Calculator */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Calcolatore Risparmio (opzionale)</label>
          <p className="text-xs text-slate-400 mb-2">Seleziona un calcolatore per mostrare ai visitatori i risparmi stimati prima del form di contatto.</p>
          <select value={form.calculator_type || ""} onChange={(e) => set("calculator_type", e.target.value)}
            className="flex h-10 w-full sm:w-80 rounded-xl border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="landing-calculator-select">
            <option value="">Nessun calcolatore</option>
            <option value="residenziale">Residenziale (Impianto da Tetto)</option>
            <option value="balcone">Balcone (Kit 820W Plug & Play)</option>
            <option value="pmi">PMI (Piccole e Medie Imprese)</option>
          </select>
        </div>

        {/* Form Type */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Tipologia Form Contatto</label>
          <p className="text-xs text-slate-400 mb-2">Scegli il tipo di form che il cliente compilera per lasciare i dati.</p>
          <select value={form.form_type || "classic"} onChange={(e) => set("form_type", e.target.value)}
            className="flex h-10 w-full sm:w-80 rounded-xl border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="landing-form-type-select">
            <option value="classic">Form Classico (Nome, Cognome, Email, Telefono)</option>
            <option value="calculator">Form Calcolatore Risparmio (Wizard interattivo)</option>
          </select>
        </div>

        {/* ── Tracking Codes per Landing ── */}
        <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-blue-900">Tracking Codes</h3>
              <p className="text-xs text-slate-400">Associa codici Facebook Pixel e Google (GTM/GA4) a questa landing.</p>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => set("tracking_codes", [...(form.tracking_codes || []), { type: "facebook_pixel", id: "", label: "", active: true }])}
                className="text-[11px] font-bold text-blue-700 bg-blue-100 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors"
                data-testid="add-fb-pixel-btn">
                + Facebook Pixel
              </button>
              <button onClick={() => set("tracking_codes", [...(form.tracking_codes || []), { type: "google_gtm", id: "", label: "", active: true }])}
                className="text-[11px] font-bold text-blue-700 bg-blue-100 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors"
                data-testid="add-gtm-btn">
                + Google GTM
              </button>
              <button onClick={() => set("tracking_codes", [...(form.tracking_codes || []), { type: "google_analytics", id: "", label: "", active: true }])}
                className="text-[11px] font-bold text-blue-700 bg-blue-100 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors"
                data-testid="add-ga4-btn">
                + Google GA4
              </button>
            </div>
          </div>

          {(form.tracking_codes || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-3">Nessun tracking associato. Clicca sui pulsanti sopra per aggiungere.</p>
          ) : (
            <div className="space-y-2.5">
              {(form.tracking_codes || []).map((tc, i) => {
                const typeLabel = tc.type === "facebook_pixel" ? "Facebook Pixel" : tc.type === "google_gtm" ? "Google GTM" : "Google GA4";
                const typeBg = tc.type === "facebook_pixel" ? "bg-blue-600" : tc.type === "google_gtm" ? "bg-emerald-600" : "bg-amber-500";
                const placeholder = tc.type === "facebook_pixel" ? "1234567890" : tc.type === "google_gtm" ? "GTM-XXXXXXX" : "G-XXXXXXXXXX";
                return (
                  <div key={i} className={`flex items-center gap-2.5 p-3 rounded-xl border transition-colors ${tc.active ? "border-blue-200 bg-white" : "border-slate-100 bg-slate-50/50"}`}
                    data-testid={`tracking-code-${i}`}>
                    <div className={`w-8 h-8 rounded-lg ${typeBg} flex items-center justify-center shrink-0`}>
                      <span className="text-white font-black text-[8px] leading-none">{tc.type === "facebook_pixel" ? "META" : tc.type === "google_gtm" ? "GTM" : "GA4"}</span>
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input
                        value={tc.id}
                        onChange={(e) => {
                          const codes = [...(form.tracking_codes || [])];
                          codes[i] = { ...codes[i], id: e.target.value };
                          set("tracking_codes", codes);
                        }}
                        className="h-8 rounded-lg text-xs font-mono"
                        placeholder={placeholder}
                        data-testid={`tracking-code-id-${i}`}
                      />
                      <Input
                        value={tc.label || ""}
                        onChange={(e) => {
                          const codes = [...(form.tracking_codes || [])];
                          codes[i] = { ...codes[i], label: e.target.value };
                          set("tracking_codes", codes);
                        }}
                        className="h-8 rounded-lg text-xs"
                        placeholder={`Etichetta (es. Campagna ${typeLabel})`}
                        data-testid={`tracking-code-label-${i}`}
                      />
                    </div>
                    <button
                      onClick={() => {
                        const codes = [...(form.tracking_codes || [])];
                        codes[i] = { ...codes[i], active: !codes[i].active };
                        set("tracking_codes", codes);
                      }}
                      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${tc.active ? "bg-blue-500" : "bg-slate-300"}`}
                      data-testid={`tracking-code-toggle-${i}`}>
                      <div className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform"
                        style={{ transform: tc.active ? "translateX(16px)" : "translateX(2px)" }} />
                    </button>
                    <button
                      onClick={() => set("tracking_codes", (form.tracking_codes || []).filter((_, j) => j !== i))}
                      className="p-1.5 text-slate-300 hover:text-red-500 transition-colors shrink-0"
                      data-testid={`tracking-code-delete-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="bg-blue-100/50 rounded-lg p-3">
            <p className="text-[11px] text-blue-700 leading-relaxed">
              I codici tracking di questa landing si <strong>sommano</strong> a quelli globali (impostati nel pannello Tracking generale).
              Quando un lead compila il form, gli eventi di conversione vengono inviati a <strong>tutti</strong> i pixel e GTM configurati.
            </p>
          </div>
        </div>

        {/* OTP Toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <span className="text-sm font-semibold text-slate-700">Verifica OTP Telefonica</span>
            <p className="text-xs text-slate-400">Se attivo, il cliente deve verificare il numero di telefono via SMS prima di inviare.</p>
          </div>
          <button onClick={() => set("otp_required", !form.otp_required)}
            className={`relative w-12 h-6 rounded-full transition-colors ${form.otp_required ? "bg-emerald-500" : "bg-slate-300"}`}
            data-testid="landing-otp-toggle">
            <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"
              style={{ transform: form.otp_required ? "translateX(24px)" : "translateX(2px)" }} />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-700">Annulla</button>
          <button onClick={handleSave} disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-8 py-2.5 text-sm btn-scale flex items-center gap-2 disabled:opacity-50"
            data-testid="landing-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {form.id ? "Aggiorna Landing" : "Crea Landing"}
          </button>
        </div>
      </div>
    </div>
  );
}
